import { useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import {
  ArrowLeft, Building2, Beaker, GraduationCap, Globe, MapPin,
  Users, FlaskConical, BookOpen, ExternalLink,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useAcademicLabs, useAcademicPIs, useChildInstitutions } from "@/hooks/useAcademicMarket";
import { AccountPIsTab } from "@/components/accounts/tabs/AccountPIsTab";
import { AccountLabsTab } from "@/components/accounts/tabs/AccountLabsTab";
import { AccountChildrenTab } from "@/components/accounts/tabs/AccountChildrenTab";
import { AccountContactsTab } from "@/components/accounts/tabs/AccountContactsTab";
import { ClinicalTrialsTab } from "@/components/accounts/tabs/ClinicalTrialsTab";
import { NIHGrantsTab } from "@/components/accounts/tabs/NIHGrantsTab";
import { useHubAccountName } from "@/hooks/useHubResearch";

const INSTITUTION_TYPE_LABELS: Record<string, string> = {
  ai_university: "University",
  ai_research_institute: "Research Institute",
  ai_teaching_hospital: "Teaching Hospital",
  ai_medical_school: "Medical School",
  ai_cancer_center: "Cancer Center",
  ai_childrens_hospital: "Children's Hospital",
  ai_government: "Government",
  ai_tech_transfer: "Tech Transfer",
  ai_foundation: "Foundation",
  not_academic_institution: "Other",
};

const TYPE_COLORS: Record<string, string> = {
  ai_university: "bg-blue-100 text-blue-800 border-blue-200",
  ai_research_institute: "bg-purple-100 text-purple-800 border-purple-200",
  ai_teaching_hospital: "bg-green-100 text-green-800 border-green-200",
  ai_medical_school: "bg-teal-100 text-teal-800 border-teal-200",
  ai_cancer_center: "bg-red-100 text-red-800 border-red-200",
  ai_childrens_hospital: "bg-pink-100 text-pink-800 border-pink-200",
  ai_government: "bg-gray-100 text-gray-800 border-gray-200",
};

export default function AcademicInstitutionDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState("pis");

  const { data: company, isLoading } = useQuery({
    queryKey: ["company-detail", id],
    queryFn: async () => {
      const { data, error } = await supabase.from("companies").select("*").eq("id", id!).single();
      if (error) throw error;
      return data as Record<string, any>;
    },
    enabled: !!id,
  });

  const { data: hubAccount } = useHubAccountName(company?.life_science_id || null);
  const hubAccountName = hubAccount?.name || null;
  const { data: labs } = useAcademicLabs(id);
  const { data: pis } = useAcademicPIs(id);
  const { data: children } = useChildInstitutions(id);

  if (isLoading) {
    return (
      <div className="space-y-6 p-6 max-w-7xl mx-auto">
        <Skeleton className="h-8 w-20" />
        <Skeleton className="h-12 w-96" />
        <div className="grid grid-cols-4 gap-4">
          {[...Array(4)].map((_, i) => <Skeleton key={i} className="h-20" />)}
        </div>
        <Skeleton className="h-96" />
      </div>
    );
  }

  if (!company) {
    return (
      <div className="space-y-6 p-6 max-w-7xl mx-auto">
        <Card>
          <CardContent className="py-12 text-center">
            <Building2 className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
            <p className="text-muted-foreground">Institution not found</p>
            <Button variant="outline" className="mt-4" onClick={() => navigate("/academic-market")}>
              Back to Academic Market
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  const location = [company.headquarters_city, company.headquarters_state, company.headquarters_country].filter(Boolean).join(", ");
  const instType = company.ai_subcategory;

  const tabs = [
    { id: "pis", label: "PIs" },
    { id: "labs", label: "Labs" },
    { id: "children", label: "Children" },
    { id: "contacts", label: "Contacts" },
    { id: "clinical-trials", label: "Clinical Trials" },
    { id: "nih-grants", label: "NIH Grants" },
  ];

  return (
    <div className="space-y-6 max-w-7xl mx-auto p-6">
      {/* Back button */}
      <Button variant="ghost" onClick={() => navigate("/academic-market")} className="gap-2 -ml-2">
        <ArrowLeft className="h-4 w-4" />
        Back to Academic Market
      </Button>

      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4">
        <div className="flex items-start gap-4">
          <div className="h-14 w-14 rounded-lg bg-purple-100 flex items-center justify-center text-purple-700 shrink-0">
            <GraduationCap className="h-7 w-7" />
          </div>
          <div className="space-y-1">
            <h1 className="text-2xl md:text-3xl font-bold">{company.company_name}</h1>
            <div className="flex items-center gap-3 text-sm text-muted-foreground">
              {location && (
                <span className="flex items-center gap-1"><MapPin className="h-4 w-4" />{location}</span>
              )}
            </div>
            <div className="flex items-center gap-2 pt-1">
              {instType && (
                <Badge variant="outline" className={TYPE_COLORS[instType] || "bg-gray-100 text-gray-700 border-gray-200"}>
                  {INSTITUTION_TYPE_LABELS[instType] || instType}
                </Badge>
              )}
              {company.parent_company_id && (
                <Badge
                  variant="outline"
                  className="cursor-pointer hover:bg-muted"
                  onClick={() => navigate(`/academic-market/${company.parent_company_id}`)}
                >
                  Child Institution
                </Badge>
              )}
            </div>
          </div>
        </div>
        <div className="flex gap-2">
          {company.domain && (
            <Button variant="outline" size="sm" asChild>
              <a href={`https://${company.domain}`} target="_blank" rel="noopener noreferrer" className="gap-2">
                <Globe className="h-4 w-4" />Website<ExternalLink className="h-3 w-3" />
              </a>
            </Button>
          )}
          <Button variant="outline" size="sm" onClick={() => navigate(`/accounts/${id}`)}>
            <Building2 className="h-4 w-4 mr-1" />Full Account
          </Button>
        </div>
      </div>

      {/* Stat cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3">
        <Card>
          <CardContent className="p-3">
            <div className="flex items-center gap-2">
              <GraduationCap className="h-4 w-4 text-teal-600" />
              <div>
                <p className="text-xl font-bold">{pis?.length || 0}</p>
                <p className="text-xs text-muted-foreground">PIs</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-3">
            <div className="flex items-center gap-2">
              <Beaker className="h-4 w-4 text-indigo-600" />
              <div>
                <p className="text-xl font-bold">{labs?.length || 0}</p>
                <p className="text-xs text-muted-foreground">Labs</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-3">
            <div className="flex items-center gap-2">
              <Building2 className="h-4 w-4 text-purple-600" />
              <div>
                <p className="text-xl font-bold">{children?.length || 0}</p>
                <p className="text-xs text-muted-foreground">Children</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-3">
            <div className="flex items-center gap-2">
              <Users className="h-4 w-4 text-orange-600" />
              <div>
                <p className="text-xl font-bold">—</p>
                <p className="text-xs text-muted-foreground">Contacts</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-3">
            <div className="flex items-center gap-2">
              <FlaskConical className="h-4 w-4 text-blue-600" />
              <div>
                <p className="text-xl font-bold">—</p>
                <p className="text-xs text-muted-foreground">Trials</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-3">
            <div className="flex items-center gap-2">
              <BookOpen className="h-4 w-4 text-green-600" />
              <div>
                <p className="text-xl font-bold">—</p>
                <p className="text-xs text-muted-foreground">Grants</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="w-full h-12 bg-muted rounded-t-lg rounded-b-none border-b border-border justify-start gap-0 p-0 overflow-x-auto">
          {tabs.map((tab) => (
            <TabsTrigger
              key={tab.id}
              value={tab.id}
              className="rounded-none border-b-2 border-transparent data-[state=active]:border-primary data-[state=active]:bg-background data-[state=active]:shadow-none px-6 h-full text-sm font-medium whitespace-nowrap"
            >
              {tab.label}
            </TabsTrigger>
          ))}
        </TabsList>

        <TabsContent value="pis" className="mt-0">
          <Card className="rounded-t-none border-t-0">
            <CardContent className="p-0">
              <AccountPIsTab accountId={company.id} />
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="labs" className="mt-0">
          <Card className="rounded-t-none border-t-0">
            <CardContent className="p-0">
              <AccountLabsTab accountId={company.id} />
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="children" className="mt-0">
          <Card className="rounded-t-none border-t-0">
            <CardContent className="p-0">
              <AccountChildrenTab accountId={company.id} />
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="contacts" className="mt-0">
          <Card className="rounded-t-none border-t-0">
            <CardContent className="p-0">
              <AccountContactsTab accountId={company.id} companyName={company.company_name} companyDomain={company.domain} />
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="clinical-trials" className="mt-0">
          <Card className="rounded-t-none border-t-0">
            <CardContent className="p-0">
              <ClinicalTrialsTab accountId={company.id} companyName={company.company_name} hubAccountName={hubAccountName} />
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="nih-grants" className="mt-0">
          <Card className="rounded-t-none border-t-0">
            <CardContent className="p-0">
              <NIHGrantsTab accountId={company.id} companyName={company.company_name} hubAccountName={hubAccountName} />
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
